import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Transition } from 'react-transition-group';
import { FocusScope } from '@react-aria/focus';
import { useDialog } from '@react-aria/dialog';
import { useTheme, jsx } from '@emotion/react';
import { REACT_PORTAL_SECTION_ID } from '../../constants/portals/index.js';
import '../../constants/breakpoints/index.js';
import '../../constants/grid/index.js';
import '../../constants/mediaQueries/index.js';
import '../../constants/boxShadows/primary.js';
import '../../constants/boxShadows/secondary.js';
import CrossIcon from '../../icons/icons/crossIcon.js';
import Style from './style.js';

var _excluded = ["backgroundColor", "children", "onClose", "title"];

var getHtmlNode = () => {
  var _document$querySelect;

  return (_document$querySelect = document.querySelector('html')) !== null && _document$querySelect !== void 0 ? _document$querySelect : document.body;
};

var getDomNode = () => {
  var _document$getElementB;

  return (_document$getElementB = document.getElementById(REACT_PORTAL_SECTION_ID)) !== null && _document$getElementB !== void 0 ? _document$getElementB : document.body;
};
/**
 * Dialog modals shouldn't contain large content and should not scroll unless screen size dictates it. To display large amounts of content, use `Immersive modal` instead.
 *
 * Dialog modals require a user to make a choice between options and are not closable on click/tap outside. They may contain a close button if a close function is provided.
 *
 * Dialog Modals should always contain at least 1 button and the logic should close the modal at some point.
 *
 * `DialogModal.Paragraph` subcomponent may be used to add some margin to the paragraphs inside the modal body.
 */


var DialogModal = _ref => {
  var {
    backgroundColor,
    children,
    onClose,
    title = ''
  } = _ref,
      rest = _objectWithoutProperties(_ref, _excluded);

  var ref = React.useRef(null);
  /**
   * Exposes ARIA dialog role to assistive technology, i.e., announces to users with
   * screen readers that they are in a dialog component, thereby making apparent
   * additional accessibility functionality (e.g., exiting dialog with "Esc" key)
   */

  var {
    dialogProps,
    titleProps
  } = useDialog({
    role: 'alertdialog'
  }, ref);
  var theme = useTheme();
  var backgroundColorWithTheme = backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : theme.COLORS.white;
  var [isClosing, setIsClosing] = useState(false);
  var domNode = useRef(getDomNode());
  var htmlNode = useRef(getHtmlNode());
  useEffect(() => {
    domNode.current = getDomNode();
    htmlNode.current = getHtmlNode();
    var htmlNodeInstance = htmlNode.current;
    htmlNodeInstance.classList.add('no-scroll');
    return () => {
      htmlNodeInstance.classList.remove('no-scroll');
    };
  }, []);

  var handleCloseIntent = () => {
    setIsClosing(true);
    setTimeout(onClose, 350);
  };

  var handleKeyDown = event => {
    if (event.key === 'Escape') {
      handleCloseIntent();
    }
  };

  return /*#__PURE__*/ReactDOM.createPortal(jsx(Transition, {
    appear: true,
    in: !isClosing,
    timeout: {
      appear: 0,
      enter: 0,
      exit: 250
    },
    unmountOnExit: true
  }, transitionState => // eslint-disable-next-line react/jsx-props-no-spreading
  jsx(Style.Overlay, _extends({
    className: transitionState
  }, rest), jsx(FocusScope, {
    contain: true,
    restoreFocus: true,
    autoFocus: true
  }, jsx(Style.ModalContainer, _extends({
    backgroundColor: backgroundColorWithTheme,
    className: transitionState,
    onKeyDown: handleKeyDown,
    ref: ref
  }, dialogProps), jsx(Style.CrossIconContainer, {
    backgroundColor: backgroundColorWithTheme,
    onClick: handleCloseIntent,
    tabIndex: 0,
    "aria-label": "Close modal"
  }, jsx(CrossIcon, null)), !!title && jsx(Style.ModalTitle, titleProps, title), children)))), domNode.current);
};
DialogModal.Paragraph = Style.Paragraph;

export { DialogModal };
